import { Controller } from "@hotwired/stimulus"

// Manages TinyMCE (HTML) and EasyMDE (Markdown) for forum reply form.
// Syncs content to textarea before form validation to fix "invalid form control" when editor hides textarea.
export default class extends Controller {
  static values = {
    textareaId: { type: String, default: "forum_post_raw_content" },
    formatSelectId: { type: String, default: "forum_post_content_format" }
  }

  connect() {
    this.tinymceInstance = null
    this.easymdeInstance = null
    this.contentValue = ""

    this.textarea = document.getElementById(this.textareaIdValue)
    this.formatSelect = document.getElementById(this.formatSelectIdValue)
    if (!this.textarea || !this.formatSelect) return

    this.contentValue = this.textarea.value || ""
    this.formatSelect.addEventListener("change", () => this.switchEditor())

    const form = this.textarea.form || document.getElementById("forum-reply-form")
    if (form) {
      const submitBtn = form.querySelector('button[type="submit"]') || document.getElementById("forum-reply-submit-btn")
      if (submitBtn) {
        submitBtn.addEventListener("click", () => this.syncToTextarea(), { capture: true })
      }
      form.addEventListener("submit", () => this.syncToTextarea(), { capture: true })
    }

    this.initEditor(this.formatSelect.value)
  }

  disconnect() {
    this.destroyEditors()
  }

  switchEditor() {
    this.syncFromEditor()
    this.destroyEditors()
    this.initEditor(this.formatSelect.value)
  }

  syncFromEditor() {
    if (this.tinymceInstance) {
      this.contentValue = this.tinymceInstance.getContent()
    } else if (this.easymdeInstance) {
      this.contentValue = this.easymdeInstance.value()
    } else if (this.textarea) {
      this.contentValue = this.textarea.value
    }
  }

  syncToTextarea() {
    this.syncFromEditor()
    if (this.textarea) {
      this.textarea.value = this.contentValue
    }
  }

  destroyEditors() {
    if (this.tinymceInstance) {
      this.contentValue = this.tinymceInstance.getContent()
      this.tinymceInstance.remove()
      this.tinymceInstance = null
    }
    if (this.easymdeInstance) {
      this.contentValue = this.easymdeInstance.value()
      this.easymdeInstance.toTextArea()
      this.easymdeInstance = null
    }
  }

  initEditor(format) {
    if (format === "markdown") {
      this.initEasyMDE()
    } else {
      this.initTinyMCE()
    }
  }

  initTinyMCE() {
    if (typeof tinymce === "undefined") {
      setTimeout(() => this.initTinyMCE(), 100)
      return
    }
    const ta = this.textarea
    if (!ta) return

    tinymce.remove(`#${this.textareaIdValue}`)
    tinymce.init({
      selector: `#${this.textareaIdValue}`,
      height: 200,
      menubar: false,
      plugins: "lists link code",
      toolbar: "undo redo | formatselect | bold italic | bullist numlist | link | removeformat | code",
      content_style: "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; line-height: 1.5; }",
      promotion: false,
      branding: false,
      setup: (editor) => {
        this.tinymceInstance = editor
        editor.on("init", () => editor.setContent(this.contentValue))
        editor.on("change", () => editor.save())
      }
    })
  }

  initEasyMDE() {
    if (typeof EasyMDE === "undefined") {
      setTimeout(() => this.initEasyMDE(), 100)
      return
    }
    const ta = this.textarea
    if (!ta) return

    ta.value = this.contentValue
    this.easymdeInstance = new EasyMDE({
      element: ta,
      spellChecker: false,
      autosave: { enabled: false },
      toolbar: ["bold", "italic", "heading", "|", "quote", "unordered-list", "ordered-list", "|", "link", "|", "preview", "side-by-side", "|", "guide"],
      minHeight: "200px",
      status: false
    })
  }
}
